import { Injectable } from '@angular/core';
import { Restangular } from 'ngx-restangular';
import { LesseeSelection, LpisLesseeModel } from '@app/ps/models/lessee.model';
import { ProjectSubjectModel } from '@app/ps/models/project-subject.model';

const LESSEES_PAGE_SIZE = 500;

/**
 * Lessees are project subjects with discriminator E, they are managed by the generic 'lessees' project subject resource
 * (the same way as administrators). This service adds the selection of a lessee on a case.
 */
@Injectable({
  providedIn: 'root'
})
export class LesseeService {

  constructor(
    private restangular: Restangular,
  ) { }

  async getAll(): Promise<ProjectSubjectModel[]> {
    const lessees: ProjectSubjectModel[] = [];
    let total = Infinity;
    // the backend list is paged, so load page by page until everything is fetched
    while (lessees.length < total) {
      const response = await this.restangular
        .all('lessees')
        .customPOST({
          filter: {
            offset: lessees.length,
            limit: LESSEES_PAGE_SIZE,
            filters: {
              searchText: { values: [] }
            }
          }
        }).toPromise();
      // the response interceptor turns a list response into an array with itemCount
      const data = response.plain();
      const page: ProjectSubjectModel[] = Array.isArray(data) ? data : data.items;
      lessees.push(...page);
      const itemCount = response.itemCount !== undefined ? response.itemCount : data.itemCount;
      total = typeof itemCount === 'number' ? itemCount : lessees.length;
      if (page.length < LESSEES_PAGE_SIZE) {
        break;
      }
    }
    return lessees;
  }

  async getLpisCandidates(): Promise<LpisLesseeModel[]> {
    const response = await this.restangular.all('lessees').customGET('lpis').toPromise();
    return response.plain().items;
  }

  async createFromLpis(lpisUzivatelId: number): Promise<ProjectSubjectModel> {
    const response = await this.restangular.one('lessees/lpis', lpisUzivatelId).customPOST({}).toPromise();
    return response.plain();
  }

  /**
   * Returns the selected lessee, an LPIS user is turned into a lessee at this moment.
   */
  async resolveSelection(selection: LesseeSelection): Promise<ProjectSubjectModel | undefined> {
    if (!selection.lessee && selection.lpis) {
      selection.lessee = await this.createFromLpis(selection.lpis.id);
      selection.lpis = undefined;
    }
    return selection.lessee || undefined;
  }

  /**
   * Sets the lessee of an existing case, an empty lessee removes it.
   */
  async updateCaseLessee(caseId: number, lessee?: ProjectSubjectModel): Promise<ProjectSubjectModel | undefined> {
    const response = await this.restangular
      .one('cases', caseId)
      .one('lessee')
      .customPUT(lessee ? { id: lessee.id } : {})
      .toPromise();
    const result = response && response.plain ? response.plain() : response;
    return result && result.id ? result : undefined;
  }
}
